import React, { useEffect, useState } from "react";
import {
  Check,
  Electronic,
  Plus,
  Minus,
  Default,
  Gas,
  Plumbing,
  Exhaust,
} from "../../assets/svg";
import { getOutlet } from "../../redux/accounts/slice";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import { createProduct } from "../../redux/product/slice";
import { toast } from "react-toastify";

const ProductDetails = ({ setShowProductModal, selectedProduct }) => {
  const dispatch = useDispatch();
  const companyData = JSON.parse(sessionStorage.getItem("companyData") || "{}")
    ?.company?.id;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const images = selectedProduct?.images || [];
  const imageUrls = images.map((img) => img.document?.url || "");
  const [quantity, setQuantity] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);

  // Selector
  const { outletList } = useSelector((store) => ({
    outletList: store?.accountsData?.getOutletStatus?.data,
  }));

  useEffect(() => {
    dispatch(
      getOutlet({
        search: "",
        pageCount: "",
        pageLimit: "all",
        company_id: companyData || "",
        sort_by: "name",
        sort_direction: "asc",
        status: "active",
        outlet: "",
        manager: "",
      })
    );
  }, [companyData]);

  const handleIncrease = () => setQuantity((prev) => prev + 1);
  const handleDecrease = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const handleOutletChange = (selectedOption) => {
    setSelectedOutlet(selectedOption);
  };

  const handleProductInterest = async () => {
    const payload = {
      product_id: selectedProduct?.id,
      outlet_id: selectedOutlet?.value,
      quantity: quantity,
    };

    try {
      const res = await dispatch(createProduct(payload));
      if (res?.type === "createProduct/fulfilled") {
        setShowSuccessModal(true);
      }
    } catch (error) {
      toast.error("Error creating product:", error);
    }
  };

  const renderCategoryIcon = (categoryName) => {
    switch (categoryName) {
      case "Electrical":
        return <Electronic />;
      case "Gas":
        return <Gas />;
      case "Plumbing":
        return <Plumbing />;
      case "Exhaust":
        return <Exhaust />;
      default:
        return <Default />;
    }
  };

  return (
    <>
      <div className="fixed top-[37px] left-0 right-0 bottom-0 z-[9999] bg-black/40 backdrop-blur-sm">
        <div className="absolute right-0 top-0 bottom-0 h-full w-[80%] md:w-[40%] sm:w-[500px] bg-white shadow-lg z-50 overflow-y-auto transition-all flex flex-col p-4">
          <div className="p-4 overflow-y-auto flex-1 space-y-4 bg-white ">
            {/* Main Product Image */}
            <img
              src={imageUrls[selectedImageIndex]}
              alt="Product"
              className="w-full h-48 object-contain rounded"
            />

            {/* Product Info */}
            <div>
              <h4 className="text-xl font-semibold font-Proxima">
                {selectedProduct?.name}
              </h4>
              <p className="text-[10px] text-gray-dark font-normal font-Proxima flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                  {renderCategoryIcon(selectedProduct?.category_name)}
                </span>
                {selectedProduct?.category_name}
              </p>
              <p className="text-lg font-semibold text-themeBtn">
                ${selectedProduct?.price}
              </p>
            </div>

            {/* Image Thumbnails */}
            <div className="flex items-center gap-2 overflow-x-auto py-2">
              {imageUrls.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`border rounded cursor-pointer p-1 ${
                    selectedImageIndex === idx
                      ? "border-themeBtn"
                      : "border-gray"
                  }`}
                >
                  <img
                    src={img}
                    alt={`Preview ${idx}`}
                    className="w-20 h-14 object-contain"
                  />
                </div>
              ))}
            </div>

            {/* Description */}
            <p className="text-sm text-gray-dark font-Proxima">
              {selectedProduct?.description}
            </p>

            {/* Specification */}
            <div>
              <p className="font-medium text-base font-Proxima">
                Specification:
              </p>
              <p className="text-sm text-gray-dark font-Proxima">
                {selectedProduct?.specification}
              </p>
            </div>

            {/* Select Outlet */}
            <div className="w-full mt-4">
              <ReactSelect
                className={"w-full"}
                id="outlet"
                placeholder="Outlet Name"
                options={(Array.isArray(outletList?.data)
                  ? outletList?.data
                  : outletList?.data?.data || []
                ).map((outlet) => ({
                  value: outlet.id,
                  label: outlet.name,
                }))}
                getOptionValue={(option) => option?.value}
                getOptionLabel={(option) => option?.label}
                value={selectedOutlet}
                onChange={handleOutletChange}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: `1px solid #0000003B`,
                    borderColor: selectedOutlet ? "#F15B29" : "#0000003B",
                    borderRadius: "0.3rem",
                    boxShadow: "none",
                    "&:hover": {
                      border: "1px solid #0000003B",
                    },
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? "#F15B29" : "white",
                    "&:active": {
                      backgroundColor: state.isSelected
                        ? "#F15B29"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                  }),
                }}
              />
            </div>

            {/* Quantity */}
            <div className="flex items-center justify-between mt-4">
              <label className="text-sm font-medium font-Proxima">
                Quantity
              </label>
              <div className="flex items-center rounded bg-bgLightTheme p-1">
                <button
                  onClick={handleIncrease}
                  className="px-3 py-1 text-lg font-medium"
                >
                  <Plus className="fill-black" />
                </button>
                <span className="px-4">{quantity}</span>
                <button
                  onClick={handleDecrease}
                  className="px-3 py-1 text-lg font-medium"
                >
                  <Minus className="fill-black" />
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white px-4 py-4 border-t border-lightgrey">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowProductModal(false)}
                className="w-full sm:w-1/2 border border-black text-black py-2 rounded uppercase font-Proxima text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleProductInterest}
                disabled={selectedOutlet === null}
                className="w-full sm:w-1/2 bg-themeBtn text-white py-2 rounded uppercase font-Proxima text-sm
             disabled:bg-themeBtn/50 disabled:cursor-not-allowed"
              >
                Express Interest
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 sm:p-8 text-center shadow-xl w-[90%] max-w-sm animate-fade-in-scale relative">
            <button
              className="absolute top-2 right-3 text-gray text-2xl hover:text-black"
              onClick={() => {
                setShowSuccessModal(false);
                setShowProductModal(false);
              }}
            >
              &times;
            </button>
            <div className="flex flex-col items-center">
              <Check />
              <p className="text-xl sm:text-2xl font-light mt-4 font-Montserrat">
                Interest Sent!
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetails;
